import { useState, useCallback, useRef, useEffect } from "react";
import { Box, Typography, useTheme } from "@mui/material";

const DEFAULT_GAP_PX = 6;
const COLLAPSED_SIZE_PX = 28;

/**
 * ResizableSplitPane — renders two children separated by a draggable divider.
 *
 * Features:
 *  - Rounded-corner pane containers with gap between them
 *  - When gap=0: panes merge seamlessly (sharp adjacent corners, zero spacing)
 *  - Optional titles that appear when a pane is collapsed to minimum
 *
 * Props:
 *  - orientation: "horizontal" | "vertical"
 *  - initialSplit: number (0-1, default 0.5)
 *  - minSizePx: number (min pane size, default COLLAPSED_SIZE_PX)
 *  - gap: number (px between panes, default 6; 0 = seamless merge)
 *  - firstTitle: string — label when first pane is collapsed
 *  - secondTitle: string — label when second pane is collapsed
 *  - children: exactly two React elements
 */
export default function ResizableSplitPane({
    orientation = "horizontal",
    initialSplit = 0.5,
    minSizePx = COLLAPSED_SIZE_PX,
    gap = DEFAULT_GAP_PX,
    firstTitle,
    secondTitle,
    firstPaneRadius,
    secondPaneRadius,
    children,
}) {
    const theme = useTheme();
    const containerRef = useRef(null);
    const [split, setSplit] = useState(initialSplit);
    const [containerSize, setContainerSize] = useState(0);
    const isDragging = useRef(false);

    const isHorizontal = orientation === "horizontal";
    const isSeamless = gap === 0;

    // Track container size for collapsed detection
    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const size = isHorizontal
                    ? entry.contentRect.width
                    : entry.contentRect.height;
                setContainerSize(size);
            }
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [isHorizontal]);

    const handlePointerDown = useCallback(
        (event) => {
            event.preventDefault();
            isDragging.current = true;
            document.body.style.cursor = isHorizontal
                ? "col-resize"
                : "row-resize";
            document.body.style.userSelect = "none";
        },
        [isHorizontal],
    );

    useEffect(() => {
        const handlePointerMove = (event) => {
            if (!isDragging.current || !containerRef.current) return;

            const rect = containerRef.current.getBoundingClientRect();
            const totalSize = isHorizontal ? rect.width : rect.height;
            const offset = isHorizontal
                ? event.clientX - rect.left
                : event.clientY - rect.top;

            const minFraction = minSizePx / totalSize;
            const maxFraction = 1 - minFraction;
            const newSplit = Math.min(
                maxFraction,
                Math.max(minFraction, offset / totalSize),
            );

            setSplit(newSplit);
        };

        const handlePointerUp = () => {
            if (!isDragging.current) return;
            isDragging.current = false;
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };

        window.addEventListener("pointermove", handlePointerMove);
        window.addEventListener("pointerup", handlePointerUp);

        return () => {
            window.removeEventListener("pointermove", handlePointerMove);
            window.removeEventListener("pointerup", handlePointerUp);
        };
    }, [isHorizontal, minSizePx]);

    const childArray = Array.isArray(children) ? children : [children];
    const firstChild = childArray[0];
    const secondChild = childArray[1];

    if (!secondChild) {
        return <PaneContainer borderRadius={2}>{firstChild}</PaneContainer>;
    }

    // Layout math — seamless mode has no handle/gap taking space
    const handleLayoutSize = isSeamless ? 0 : 6;
    const totalChrome = gap * 2 + handleLayoutSize;
    const firstSize = `calc(${split * 100}% - ${totalChrome / 2}px)`;
    const secondSize = `calc(${(1 - split) * 100}% - ${totalChrome / 2}px)`;

    // Collapsed detection — only after the container has been measured.
    // Before the first ResizeObserver callback, containerSize is 0 and all
    // panes would incorrectly appear collapsed (display: none), which breaks
    // autoFocus and other mount-time side-effects in child components.
    const hasMeasured = containerSize > 0;
    const firstPanePx = containerSize * split;
    const secondPanePx = containerSize * (1 - split);
    const collapseThreshold = minSizePx + 8;
    const isFirstCollapsed = hasMeasured && firstPanePx <= collapseThreshold;
    const isSecondCollapsed = hasMeasured && secondPanePx <= collapseThreshold;

    // Corner radius logic:
    // - With gap: both panes fully rounded
    // - Seamless: only outer corners rounded, adjacent corners sharp (0)
    // - Override: firstPaneRadius / secondPaneRadius take precedence
    const radius = 8; // px, matches borderRadius: 2 in MUI (2 * 4px)
    let firstRadius, secondRadius;
    if (isSeamless) {
        if (isHorizontal) {
            firstRadius = `${radius}px 0 0 ${radius}px`;
            secondRadius = `0 ${radius}px ${radius}px 0`;
        } else {
            firstRadius = `${radius}px ${radius}px 0 0`;
            secondRadius = `0 0 ${radius}px ${radius}px`;
        }
    } else {
        firstRadius = `${radius}px`;
        secondRadius = `${radius}px`;
    }
    // Allow explicit overrides
    if (firstPaneRadius !== undefined) firstRadius = firstPaneRadius;
    if (secondPaneRadius !== undefined) secondRadius = secondPaneRadius;

    return (
        <Box
            ref={containerRef}
            sx={{
                display: "flex",
                flexDirection: isHorizontal ? "row" : "column",
                width: "100%",
                height: "100%",
                overflow: "hidden",
                gap: `${gap}px`,
            }}
        >
            <PaneContainer
                borderRadius={firstRadius}
                sx={{
                    [isHorizontal ? "width" : "height"]: firstSize,
                    flexShrink: 0,
                }}
            >
                {isFirstCollapsed && firstTitle && (
                    <CollapsedLabel
                        label={firstTitle}
                        orientation={orientation}
                    />
                )}
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        display:
                            isFirstCollapsed && firstTitle ? "none" : "block",
                        overflow: "hidden",
                    }}
                >
                    {firstChild}
                </Box>
            </PaneContainer>

            {/* Drag handle */}
            {isSeamless ? (
                // Seamless: zero-width layout, but a wide invisible hit area for dragging
                <Box
                    onPointerDown={handlePointerDown}
                    sx={{
                        position: "relative",
                        [isHorizontal ? "width" : "height"]: 0,
                        flexShrink: 0,
                        zIndex: 10,
                    }}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            [isHorizontal ? "width" : "height"]: "12px",
                            [isHorizontal ? "left" : "top"]: "-6px",
                            [isHorizontal ? "height" : "width"]: "100%",
                            cursor: isHorizontal ? "col-resize" : "row-resize",
                        }}
                    />
                </Box>
            ) : (
                // Gapped: visible drag indicator
                <Box
                    onPointerDown={handlePointerDown}
                    sx={{
                        [isHorizontal ? "width" : "height"]:
                            `${handleLayoutSize}px`,
                        flexShrink: 0,
                        cursor: isHorizontal ? "col-resize" : "row-resize",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        "&:hover": {
                            "& .drag-indicator": {
                                backgroundColor: theme.palette.primary.main,
                                opacity: 1,
                            },
                        },
                    }}
                >
                    <Box
                        className="drag-indicator"
                        sx={{
                            [isHorizontal ? "width" : "height"]: "2px",
                            [isHorizontal ? "height" : "width"]: "40px",
                            borderRadius: "2px",
                            backgroundColor: theme.palette.divider,
                            opacity: 0.4,
                            transition: "all 0.15s ease",
                        }}
                    />
                </Box>
            )}

            {/* Second pane */}
            <PaneContainer
                borderRadius={secondRadius}
                sx={{
                    [isHorizontal ? "width" : "height"]: secondSize,
                    flexShrink: 0,
                }}
            >
                {isSecondCollapsed && secondTitle && (
                    <CollapsedLabel
                        label={secondTitle}
                        orientation={orientation}
                    />
                )}
                <Box
                    sx={{
                        width: "100%",
                        height: "100%",
                        display:
                            isSecondCollapsed && secondTitle ? "none" : "block",
                        overflow: "hidden",
                    }}
                >
                    {secondChild}
                </Box>
            </PaneContainer>
        </Box>
    );
}

/**
 * PaneContainer — rounded-corner wrapper for each pane.
 * No background/border — gaps provide the visual boundary.
 */
function PaneContainer({ children, borderRadius = 2, sx = {} }) {
    return (
        <Box
            sx={{
                overflow: "hidden",
                borderRadius,
                ...sx,
            }}
        >
            {children}
        </Box>
    );
}

/**
 * CollapsedLabel — shown when a pane is at minimum size.
 */
function CollapsedLabel({ label, orientation }) {
    const theme = useTheme();
    const isHorizontal = orientation === "horizontal";

    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: theme.palette.background.default,
            }}
        >
            <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{
                    writingMode: isHorizontal ? "vertical-rl" : "horizontal-tb",
                    textOrientation: "mixed",
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    fontSize: 11,
                    opacity: 0.6,
                    userSelect: "none",
                }}
            >
                {label}
            </Typography>
        </Box>
    );
}
